import { ReactNode } from 'react'
import styled, { createGlobalStyle, css, FlattenSimpleInterpolation } from 'styled-components'
import { media } from 'utils/media'

export const FontDefinitions = createGlobalStyle`
  @font-face {
    font-family: 'ABCMonumentGrotesk-Bold';
    src: url('https://cdn.thingtesting.com/fonts/MonumentGrotesk/ABCMonumentGrotesk-Bold.woff2') format('woff2'),
         url('https://cdn.thingtesting.com/fonts/MonumentGrotesk/ABCMonumentGrotesk-Bold.woff') format('woff');
    font-display: swap;
  }

  @font-face {
    font-family: 'ABCMonumentGrotesk-Medium';
    src: url('https://cdn.thingtesting.com/fonts/MonumentGrotesk/ABCMonumentGrotesk-Medium.woff2') format('woff2'),
         url('https://cdn.thingtesting.com/fonts/MonumentGrotesk/ABCMonumentGrotesk-Medium.woff') format('woff');
    font-display: swap;
  }

  @font-face {
    font-family: 'ABCMonumentGrotesk-Regular';
    src: url('https://cdn.thingtesting.com/fonts/MonumentGrotesk/ABCMonumentGrotesk-Regular.woff2') format('woff2'),
         url('https://cdn.thingtesting.com/fonts/MonumentGrotesk/ABCMonumentGrotesk-Regular.woff') format('woff');
    font-display: swap;
  }

  body {
    --monument-grotesk-bold: 'ABCMonumentGrotesk-Bold', Helvetica, sans-serif;
    --monument-grotesk-medium: 'ABCMonumentGrotesk-Medium', Helvetica, sans-serif;
    --monument-grotesk-regular: 'ABCMonumentGrotesk-Regular', Helvetica, sans-serif;
    
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    
    font-family: var(--monument-grotesk-regular);
    color: var(--neutral-80);
  }
`

export const sizes = ['xxl', 'xl', 'l', 'm', 's', 'p', 'ps', 'pxs', 'small', 'extra-small'] as const

export const weights = ['regular', 'medium', 'semi', 'bold'] as const

export const colors = ['neutral-100', 'neutral-80', 'neutral-60', 'neutral-40', 'white', 'error-red', 'red-60', 'red-80', 'green-80'] as const

export const margins = ['0x', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x'] as const

export const aligns = ['start', 'center', 'end']

export type Size = (typeof sizes)[number]
export type Weight = (typeof weights)[number]
export type Color = (typeof colors)[number]
export type Margin = (typeof margins)[number]
export type Align = (typeof aligns)[number]

export interface Props {
  size?: Size
  weight?: Weight
  color?: Color
  shouty?: boolean
  margin?: Margin
  nowrap?: boolean
  align?: Align
  children?: ReactNode | ReactNode[]
}

/**
 * font-size: 64px \
 * line-height: 64px
 */
export function textSize(size: 'xxl'): FlattenSimpleInterpolation

/**
 * font-size: 48px \
 * line-height: 56px
 */
export function textSize(size: 'xl'): FlattenSimpleInterpolation

/**
 * font-size: 36px \
 * line-height: 40px
 */
export function textSize(size: 'l'): FlattenSimpleInterpolation

/**
 * font-size: 24px \
 * line-height: 32px
 */
export function textSize(size: 'm'): FlattenSimpleInterpolation

/**
 * font-size: 20px \
 * line-height: 24px
 */
export function textSize(size: 's'): FlattenSimpleInterpolation

/**
 * font-size: 18px \
 * line-height: 24px
 */
export function textSize(size: 'p'): FlattenSimpleInterpolation

/**
 * font-size: 16px \
 * line-height: 24px
 */
export function textSize(size: 'ps'): FlattenSimpleInterpolation

/**
 * font-size: 14px \
 * line-height: 20px
 */
export function textSize(size: 'pxs'): FlattenSimpleInterpolation

/**
 * font-size: 12px \
 * line-height: 16px
 */
export function textSize(size: 'small'): FlattenSimpleInterpolation

/**
 * font-size: 8px \
 * line-height: 6px
 */
export function textSize(size: 'extra-small'): FlattenSimpleInterpolation

export function textSize(size: Size): FlattenSimpleInterpolation
export function textSize(size: Size = 'p') {
  switch (size) {
    case 'xxl':
      return css`
        font-size: var(--text-size-xxl);
        line-height: var(--line-height-xxl);

        ${media.phoneDown} {
          font-size: 32px;
          line-height: 40px;
        }
      `

    case 'xl':
      return css`
        font-size: var(--text-size-xl);
        line-height: var(--line-height-xl);

        ${media.phoneDown} {
          font-size: 32px;
          line-height: 40px;
        }
      `

    case 'l':
      return css`
        font-size: var(--text-size-l);
        line-height: var(--line-height-l);
      `

    case 'm':
      return css`
        font-size: var(--text-size-m);
        line-height: var(--line-height-m);
      `

    case 's':
      return css`
        font-size: var(--text-size-s);
        line-height: var(--line-height-s);
      `

    case 'p':
      return css`
        font-size: var(--text-size-p);
        line-height: var(--line-height-p);
      `

    case 'ps':
      return css`
        font-size: var(--text-size-ps);
        line-height: var(--line-height-ps);
      `

    case 'pxs':
      return css`
        font-size: var(--text-size-pxs);
        line-height: var(--line-height-pxs);
      `

    case 'small':
      return css`
        font-size: var(--text-size-small);
        line-height: var(--line-height-small);
      `

    case 'extra-small':
      return css`
        font-size: var(--text-size-extra-small);
        line-height: var(--line-height-extra-small);
      `
  }
}

export const textWeight = (weight: Weight = 'regular') => {
  switch (weight) {
    case 'regular':
      return css`
        font-family: var(--monument-grotesk-regular);
        font-weight: normal;
      `

    case 'medium':
      return css`
        font-family: var(--monument-grotesk-medium);
        font-weight: normal;
      `

    case 'semi':
      return css`
        font-family: var(--monument-grotesk-medium);
        font-weight: normal;
      `

    case 'bold':
      return css`
        font-family: var(--monument-grotesk-bold);
        font-weight: normal;
      `
  }
}

export const textColor = (color: Color = 'neutral-100') => {
  if (color === 'white') {
    return css`
      color: white;
    `
  }

  return css`
    color: var(--${color});
  `
}

export const margin = (marginSize?: Margin) => {
  if (marginSize) {
    return css`
      margin-bottom: ${margins.indexOf(marginSize) * 8}px;
    `
  }
}

export const Text = styled.p<Props>`
  ${(props) => textSize(props.size)}
  ${(props) => textWeight(props.weight)}
  ${(props) => textColor(props.color)}
  ${(props) => margin(props.margin)}

  ${(props) =>
    props.shouty &&
    css`
      letter-spacing: 0.08em;
      text-transform: uppercase;
    `}

  ${(props) =>
    props.shouty === false &&
    css`
      text-transform: none;
    `}
  
  ${(props) =>
    props.nowrap === true &&
    css`
      white-space: nowrap;
    `}

    ${(props) =>
    props.align &&
    css`
      text-align: ${props.align};
    `}
`

export const Paragraph = styled.p<{ margin?: Margin; variant?: 'normal' | 'small' | 'giant' }>`
  font-family: var(--monument-grotesk-regular);
  font-weight: normal;
  font-size: 20px;
  line-height: 28px;
  ${(props) =>
    props.margin
      ? margin(props.margin)
      : css`
          margin-bottom: 20px;
        `}

  ${(props) =>
    props.variant === 'small' &&
    css`
      font-size: 18px;
      line-height: 26px;
    `}

    ${(props) =>
    props.variant === 'giant' &&
    css`
      font-size: 28px;
      line-height: 41px;
    `}

  ${media.phoneDown} {
    font-size: 18px;
    line-height: 26px;
  }
`

/**
 * Helpers
 */
export const ContentPageTitle = styled.h1`
  ${textSize('xxl')}
  ${textWeight('medium')}
  ${margin('6x')}

  margin-top: 80px;
  text-align: left;

  ${media.phoneDown} {
    margin-top: 20px;
  }
`
